// src/services/whatsappService.js
import { db } from '../firebase/config';
import { 
  collection, 
  addDoc, 
  serverTimestamp, 
  getDoc, 
  doc 
} from 'firebase/firestore';

// Twilio configuration
const TWILIO_CONFIG = {
  accountSid: 'AC95e5076696907732399f7b47b698ae8a',
  authToken: '671d21b0ed1bc9f3e5a454b049bcb9bd',
  twilioNumber: '+14155238886',
  adminNumber: '+917034232439'
};

// Basic WhatsApp message sender
const sendWhatsAppMessage = async (to, body) => {
  try {
    const response = await fetch(
      `https://api.twilio.com/2010-04-01/Accounts/${TWILIO_CONFIG.accountSid}/Messages.json`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Authorization': 'Basic ' + btoa(`${TWILIO_CONFIG.accountSid}:${TWILIO_CONFIG.authToken}`)
        },
        body: new URLSearchParams({
          From: `whatsapp:${TWILIO_CONFIG.twilioNumber}`,
          To: `whatsapp:${to}`,
          Body: body
        }).toString()
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Twilio API Error:', errorText);
      throw new Error(`Failed to send WhatsApp message: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error sending WhatsApp message:', error);
    throw error;
  }
};

// Send Party Hall Enquiry
export const sendPartyHallEnquiry = async (enquiryData) => {
  try {
    const messageBody = `Your code is: 1234

*New Party Hall Enquiry!*

*Contact Details*
Name: ${enquiryData.name}
Phone: ${enquiryData.phone}
Email: ${enquiryData.email || 'Not provided'}

*Event Details*
Date: ${new Date(enquiryData.date).toLocaleDateString()}
Number of Guests: ${enquiryData.guestCount || 'Not specified'}
Event Type: ${enquiryData.eventType || 'Not specified'}

*Additional Message*
${enquiryData.message || 'None'}

*Enquiry Time*
${new Date().toLocaleString()}`;

    await sendWhatsAppMessage(TWILIO_CONFIG.adminNumber, messageBody);
    return { success: true };
  } catch (error) {
    console.error('Error in sendPartyHallEnquiry:', error);
    return { success: false, error: error.message };
  }
};

// Send Payment Verification
export const sendPaymentVerification = async (paymentData) => {
  try {
    // Convert image to base64 for Firestore storage
    let paymentProofBase64 = null;
    if (paymentData.paymentProof) {
      const reader = new FileReader();
      paymentProofBase64 = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(paymentData.paymentProof);
      });
    }

    // Save to Firestore
    const paymentDoc = {
      amount: paymentData.amount,
      utrNumber: paymentData.utrNumber,
      transactionId: paymentData.transactionId,
      customerName: `${paymentData.firstName} ${paymentData.lastName}`,
      customerPhone: paymentData.phone,
      checkIn: paymentData.checkIn,
      checkOut: paymentData.checkOut,
      rooms: paymentData.rooms,
      paymentProof: paymentProofBase64,
      timestamp: serverTimestamp(),
      status: 'pending',
      bookingDetails: {
        ...paymentData,
        paymentProof: null // Remove File object before saving
      }
    };

    // Save to Firestore
    const paymentRef = await addDoc(collection(db, 'payments'), paymentDoc);
    const paymentId = paymentRef.id;

    // Send initial message with details
    const messageBody = `Your code is: 1234

*New Payment Verification Required*

*Transaction Details*
Payment ID: ${paymentId}
Amount: ₹${paymentData.amount}
UTR Number: ${paymentData.utrNumber}
Transaction ID: ${paymentData.transactionId}

*Guest Details*
Name: ${paymentData.firstName} ${paymentData.lastName}
Phone: ${paymentData.phone}

*Booking Details*
Check-in: ${new Date(paymentData.checkIn).toLocaleDateString()}
Check-out: ${new Date(paymentData.checkOut).toLocaleDateString()}
Rooms: ${paymentData.rooms}

*Verification Time*
${new Date().toLocaleString()}

Payment details saved with ID: ${paymentId}
UTR Number: ${paymentData.utrNumber}

A payment screenshot has been saved to the database for verification.`;

    // Send text message
    await sendWhatsAppMessage(TWILIO_CONFIG.adminNumber, messageBody);

    return { 
      success: true, 
      paymentId,
      message: 'Payment verification sent successfully'
    };
  } catch (error) {
    console.error('Error in sendPaymentVerification:', error);
    return {
      success: false,
      error: error.message || 'Failed to send payment verification'
    };
  }
};

// Send Booking Notification
export const sendBookingNotification = async (bookingData) => {
  try {
    const messageBody = `Your code is: 1234

*New Booking Alert!*

*Guest Details*
Name: ${bookingData.firstName} ${bookingData.lastName}
Phone: ${bookingData.phone}
Email: ${bookingData.email || 'Not provided'}

*Booking Details*
Check-in: ${new Date(bookingData.checkIn).toLocaleDateString()}
Check-out: ${new Date(bookingData.checkOut).toLocaleDateString()}
Rooms: ${bookingData.rooms}
Adults: ${bookingData.adults}
Children: ${bookingData.children || 0}

*Payment Details*
Amount: ₹${bookingData.amount || 'Not specified'}
UTR Number: ${bookingData.utrNumber || 'Not provided'}
Transaction ID: ${bookingData.transactionId || 'Not provided'}

*Special Requests*
${bookingData.specialRequests || 'None'}

*Booking Time*
${new Date().toLocaleString()}`;

    await sendWhatsAppMessage(TWILIO_CONFIG.adminNumber, messageBody);
    return { success: true };
  } catch (error) {
    console.error('Error in sendBookingNotification:', error);
    return { success: false, error: error.message };
  }
};

// Send Booking Confirmation
export const sendBookingConfirmation = async (bookingData) => {
  try {
    const messageBody = `Thank you for your booking at East Fort Resorts!

*Your Booking Details*
Check-in: ${new Date(bookingData.checkIn).toLocaleDateString()}
Check-out: ${new Date(bookingData.checkOut).toLocaleDateString()}
Rooms: ${bookingData.rooms}
Amount Paid: ₹${bookingData.amount}

We will verify your payment and confirm your booking shortly.
For any assistance, please contact us.`;

    await sendWhatsAppMessage(bookingData.phone, messageBody);
    return { success: true };
  } catch (error) {
    console.error('Error in sendBookingConfirmation:', error);
    return { success: false, error: error.message };
  }
};

// Fetch payment details from Firestore
export const fetchPaymentDetails = async (paymentId) => {
  try {
    const paymentDoc = await getDoc(doc(db, 'payments', paymentId));
    if (paymentDoc.exists()) {
      return {
        success: true,
        data: paymentDoc.data()
      };
    } else {
      throw new Error('Payment not found');
    }
  } catch (error) {
    console.error('Error fetching payment:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Test function
export const testWhatsAppService = async () => {
  const testData = {
    firstName: 'Test',
    lastName: 'User',
    phone: '+917034232439',
    email: 'test@example.com',
    checkIn: new Date(),
    checkOut: new Date(Date.now() + 86400000),
    rooms: 1,
    adults: 2,
    children: 0,
    specialRequests: 'Test booking'
  };

  return sendBookingNotification(testData);
};